import React from 'react'
import { History } from 'history'

import { WorkspaceLogo } from '@sketch/components'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

import { CommandBarItem, CommandBarSection } from '../types'
import { routes } from '../../routes'

import { ReactComponent as BellIcon } from '@sketch/icons/misc-notification-17'
import { ReactComponent as BillingIcon } from '@sketch/icons/card-default-17'
import { ReactComponent as DeviceDesktopIcon } from '@sketch/icons/device-desktop-17'
import { ReactComponent as DocumentsIcon } from '@sketch/icons/documents-2-17'
import { ReactComponent as GroupsIcon } from '@sketch/icons/group-circle-17'
import { ReactComponent as LockIcon } from '@sketch/icons/lock-closed-17'
import { ReactComponent as IntegrationsIcon } from '@sketch/icons/img-integrations-17'
import { ReactComponent as LibraryIcon } from '@sketch/icons/link-library-17'
import { ReactComponent as MoonIcon } from '@sketch/icons/moon-17'
import { ReactComponent as PermissionsIcon } from '@sketch/icons/persons-2-17'
import { ReactComponent as PersonalSettingsIcon } from '@sketch/icons/person-circle-17'
import { ReactComponent as PreviewsIcon } from '@sketch/icons/document-magnifying-glass-17'
import { ReactComponent as SharedWithMeIcon } from '@sketch/icons/folder-person-17'
import { ReactComponent as SunIcon } from '@sketch/icons/sun-17'
import { ReactComponent as TemplatesIcon } from '@sketch/icons/templates-17'
import { ReactComponent as TrashIcon } from '@sketch/icons/trash-empty-17'
import { ReactComponent as WorkspacesIcon } from '@sketch/icons/workspace-17'

type CommandWithoutRun = OmitSafe<CommandBarItem, 'kind' | 'run'>

const isPersonalWorkspace = (workspace: WorkspaceMinimalFragment) =>
  workspace.type === 'PERSONAL'

interface WorkspaceNavigationDefinition {
  analyticsId: string
  icon: CommandBarItem['icon']
  id: string
  keywords?: readonly string[]
  priority: number
  title: string
  to:
    | typeof routes.WORKSPACE_SHARES
    | typeof routes.WORKSPACE_DRAFTS
    | typeof routes.WORKSPACE_SHARED_WITH_ME
    | typeof routes.WORKSPACE_LIBRARIES
    | typeof routes.WORKSPACE_TEMPLATES
    | typeof routes.WORKSPACE_TRASH
    | typeof routes.WORKSPACE_ARCHIVE
  when: (flags: {
    archiveEnabled: boolean
    canAdministerWorkspace: boolean
    isFinance: boolean
  }) => boolean
}

interface WorkspaceSettingsDefinition {
  analyticsId: string
  icon: CommandBarItem['icon']
  id: string
  section: CommandBarSection
  keywords?: readonly string[]
  priority: number
  title: string
  subtitle?: string
  to:
    | typeof routes.WORKSPACE_SETTINGS_SETTINGS
    | typeof routes.WORKSPACE_SETTINGS_PEOPLE
    | typeof routes.WORKSPACE_SETTINGS_BILLING
    | typeof routes.WORKSPACE_SETTINGS_SSO
    | typeof routes.WORKSPACE_SETTINGS_PERMISSION_GROUPS
    | typeof routes.WORKSPACE_SETTINGS_INTEGRATIONS
    | typeof routes.WORKSPACE_SETTINGS_DOCUMENT_PREVIEWS
}

interface AccountSettingsDefinition {
  analyticsId: string
  icon: CommandBarItem['icon']
  id: string
  section: CommandBarSection
  keywords?: readonly string[]
  priority: number
  title: string
  subtitle?: string
  to: typeof routes.PERSONAL_SETTINGS_SECURITY | typeof routes.PERSONAL_SETTINGS
}

interface CommandBarAppearance {
  isDarkMode: boolean
  toggleMatchSystem: () => void
  enableDarkMode: () => void
  enableLightMode: () => void
}

interface CreateStaticCommandsProps {
  history: History
  workspace?: WorkspaceMinimalFragment
  workspaces: WorkspaceMinimalFragment[]
  appearance: CommandBarAppearance
}

export const createCommand = (
  item: CommandWithoutRun,
  run: () => void
): CommandBarItem => ({
  ...item,
  kind: 'command',
  run,
})

export const navigateCommand = (
  item: CommandWithoutRun & {
    history: History
    to: string
  }
): CommandBarItem => {
  const { history, to, ...command } = item

  return createCommand(command, () => history.push(to))
}

export const compactCommands = (
  items: Array<CommandBarItem | false | null | undefined>
): CommandBarItem[] => items.filter(Boolean) as CommandBarItem[]

const workspaceNavigationDefinitions: readonly WorkspaceNavigationDefinition[] =
  [
    {
      id: 'navigation.documents',
      analyticsId: 'navigation.documents',
      title: 'All Documents',
      keywords: ['all documents', 'shares'],
      icon: DocumentsIcon,
      priority: 10,
      to: routes.WORKSPACE_SHARES,
      when: ({ isFinance }) => !isFinance,
    },
    {
      id: 'navigation.drafts',
      analyticsId: 'navigation.drafts',
      title: 'My Drafts',
      keywords: ['my drafts'],
      icon: DocumentsIcon,
      priority: 9,
      to: routes.WORKSPACE_DRAFTS,
      when: ({ isFinance }) => !isFinance,
    },
    {
      id: 'navigation.shared-with-me',
      analyticsId: 'navigation.shared-with-me',
      title: 'Shared with Me',
      icon: SharedWithMeIcon,
      priority: 8,
      to: routes.WORKSPACE_SHARED_WITH_ME,
      when: ({ isFinance }) => !isFinance,
    },
    {
      id: 'navigation.libraries',
      analyticsId: 'navigation.libraries',
      title: 'Libraries',
      icon: LibraryIcon,
      priority: 7,
      to: routes.WORKSPACE_LIBRARIES,
      when: ({ isFinance }) => !isFinance,
    },
    {
      id: 'navigation.templates',
      analyticsId: 'navigation.templates',
      title: 'Templates',
      icon: TemplatesIcon,
      priority: 6,
      to: routes.WORKSPACE_TEMPLATES,
      when: ({ isFinance }) => !isFinance,
    },
    {
      id: 'navigation.trash',
      analyticsId: 'navigation.trash',
      title: 'Trash',
      icon: TrashIcon,
      priority: 5,
      to: routes.WORKSPACE_TRASH,
      when: ({ canAdministerWorkspace, isFinance }) =>
        !isFinance && canAdministerWorkspace,
    },
    {
      id: 'navigation.archive',
      analyticsId: 'navigation.archive',
      title: 'Archive',
      keywords: ['archived documents'],
      icon: DocumentsIcon,
      priority: 4,
      to: routes.WORKSPACE_ARCHIVE,
      when: ({ archiveEnabled, isFinance }) => !isFinance && archiveEnabled,
    },
  ]

const workspaceSettingsDefinitions: readonly WorkspaceSettingsDefinition[] = [
  {
    id: 'settings.workspace',
    analyticsId: 'settings.workspace',
    title: 'Workspace Settings',
    keywords: ['general'],
    section: 'Workspace Settings',
    icon: WorkspacesIcon,
    priority: 4,
    to: routes.WORKSPACE_SETTINGS_SETTINGS,
  },
  {
    id: 'settings.people',
    analyticsId: 'settings.people',
    title: 'People',
    keywords: ['members'],
    section: 'Workspace Settings',
    icon: PermissionsIcon,
    priority: 4,
    to: routes.WORKSPACE_SETTINGS_PEOPLE,
  },
  {
    id: 'settings.billing',
    analyticsId: 'settings.billing',
    title: 'Billing',
    section: 'Workspace Settings',
    icon: BillingIcon,
    priority: 2,
    to: routes.WORKSPACE_SETTINGS_BILLING,
  },
  {
    id: 'settings.sso',
    analyticsId: 'settings.sso',
    title: 'Single Sign-On',
    section: 'Workspace Settings',
    keywords: ['sso'],
    icon: LockIcon,
    priority: 2,
    to: routes.WORKSPACE_SETTINGS_SSO,
  },
  {
    id: 'settings.groups',
    analyticsId: 'settings.groups',
    title: 'Groups',
    section: 'Workspace Settings',
    keywords: ['permission groups'],
    icon: GroupsIcon,
    priority: 2,
    to: routes.WORKSPACE_SETTINGS_PERMISSION_GROUPS,
  },
  {
    id: 'settings.integrations',
    analyticsId: 'settings.integrations',
    title: 'Integrations',
    section: 'Workspace Settings',
    icon: IntegrationsIcon,
    priority: 2,
    to: routes.WORKSPACE_SETTINGS_INTEGRATIONS,
  },
  {
    id: 'settings.document-previews',
    analyticsId: 'settings.document-previews',
    title: 'Document Previews',
    section: 'Workspace Settings',
    keywords: ['previews'],
    icon: PreviewsIcon,
    priority: 2,
    to: routes.WORKSPACE_SETTINGS_DOCUMENT_PREVIEWS,
  },
]

const accountSettingsDefinitions: readonly AccountSettingsDefinition[] = [
  {
    id: 'settings.personal',
    analyticsId: 'settings.personal',
    title: 'General',
    subtitle: 'Change your name, email, and profile picture',
    section: 'Account Settings',
    keywords: ['account', 'profile', 'email', 'name'],
    icon: PersonalSettingsIcon,
    priority: 2,
    to: routes.PERSONAL_SETTINGS,
  },
  {
    id: 'settings.account.security',
    analyticsId: 'settings.account.security',
    title: 'Security',
    subtitle: 'Change your password and manage two-factor authentication',
    section: 'Account Settings',
    keywords: ['general', 'password', '2fa', 'two-factor authentication'],
    icon: LockIcon,
    priority: 1,
    to: routes.PERSONAL_SETTINGS_SECURITY,
  },
]

const createWorkspaceCommands = ({
  history,
  workspace,
  workspaces,
}: Pick<
  CreateStaticCommandsProps,
  'history' | 'workspace' | 'workspaces'
>): CommandBarItem[] => {
  const globalCommands = [
    navigateCommand({
      id: 'navigation.inbox',
      analyticsId: 'navigation.inbox',
      title: 'Inbox',
      section: 'Workspace',
      keywords: ['inbox', 'notifications', 'updates'],
      icon: BellIcon,
      priority: 3,
      to: routes.UPDATES.create({}),
      history,
    }),
  ]

  const workspaceSwitcherCommands = workspaces
    .filter(item => item.identifier !== workspace?.identifier)
    .map(item =>
      navigateCommand({
        id: `workspace.switch.${item.identifier}`,
        analyticsId: 'workspace.switch',
        title: `Switch to ${item.name}`,
        subtitle: 'Workspace',
        section: 'Other Workspaces',
        keywords: ['switch workspace', 'change workspace'],
        iconNode: isPersonalWorkspace(item) ? (
          <WorkspaceLogo.Icon icon="briefcase" size="24px" />
        ) : (
          <WorkspaceLogo
            workspaceName={item.name}
            src={item.avatar?.large}
            size="24px"
          />
        ),
        to: routes.WORKSPACE_SHARES.create({ workspaceId: item.identifier }),
        history,
      })
    )

  if (!workspace) {
    return [...globalCommands, ...workspaceSwitcherCommands]
  }

  const flags = {
    archiveEnabled: workspace.features.archiveEnabled,
    canAdministerWorkspace: workspace.userCanAdminister,
    isFinance: workspace.userRole === 'FINANCE',
  }

  const workspaceNavigationCommands = compactCommands(
    workspaceNavigationDefinitions.map(item =>
      item.when(flags)
        ? navigateCommand({
            id: item.id,
            analyticsId: item.analyticsId,
            title: item.title,
            section: 'Workspace',
            keywords: item.keywords ? [...item.keywords] : undefined,
            icon: item.icon,
            priority: item.priority,
            to: item.to.create({ workspaceId: workspace.identifier }),
            history,
          })
        : false
    )
  )

  return [
    ...workspaceNavigationCommands,
    ...globalCommands,
    ...workspaceSwitcherCommands,
  ]
}

const createAccountSettingsCommands = ({
  history,
}: Pick<CreateStaticCommandsProps, 'history'>): CommandBarItem[] =>
  accountSettingsDefinitions.map(item =>
    navigateCommand({
      id: item.id,
      analyticsId: item.analyticsId,
      title: item.title,
      subtitle: item.subtitle,
      section: item.section,
      keywords: item.keywords ? [...item.keywords] : undefined,
      icon: item.icon,
      priority: item.priority,
      to: item.to.create({}),
      history,
    })
  )

const createWorkspaceSettingsCommands = ({
  history,
  workspace,
}: Pick<
  CreateStaticCommandsProps,
  'history' | 'workspace'
>): CommandBarItem[] => {
  if (!workspace?.userCanAdminister) {
    return []
  }

  return workspaceSettingsDefinitions.map(item =>
    navigateCommand({
      id: item.id,
      analyticsId: item.analyticsId,
      title: item.title,
      subtitle: item.subtitle,
      section: item.section,
      keywords: item.keywords ? [...item.keywords] : undefined,
      icon: item.icon,
      priority: item.priority,
      to: item.to.create({ workspaceId: workspace.identifier }),
      history,
    })
  )
}

const createAppearanceCommands = ({
  appearance: {
    enableDarkMode,
    enableLightMode,
    toggleMatchSystem,
    isDarkMode,
  },
}: Pick<CreateStaticCommandsProps, 'appearance'>): CommandBarItem[] =>
  compactCommands([
    createCommand(
      {
        id: 'theme.toggle',
        analyticsId: 'theme.toggle',
        title: 'Toggle Match System Theme',
        section: 'Appearance',
        keywords: ['appearance', 'dark', 'light', 'match system'],
        icon: DeviceDesktopIcon,
        priority: 0,
      },
      toggleMatchSystem
    ),
    !isDarkMode &&
      createCommand(
        {
          id: 'theme.dark',
          analyticsId: 'theme.dark',
          title: 'Enable Dark Mode',
          section: 'Appearance',
          keywords: ['appearance', 'dark', 'light'],
          icon: MoonIcon,
          priority: 1,
        },
        enableDarkMode
      ),
    isDarkMode &&
      createCommand(
        {
          id: 'theme.light',
          analyticsId: 'theme.light',
          title: 'Enable Light Mode',
          section: 'Appearance',
          keywords: ['appearance', 'light', 'dark'],
          icon: SunIcon,
          priority: 1,
        },
        enableLightMode
      ),
  ])

export const createStaticCommands = (
  props: CreateStaticCommandsProps
): CommandBarItem[] => {
  return [
    ...createWorkspaceCommands(props),
    ...createAccountSettingsCommands(props),
    ...createWorkspaceSettingsCommands(props),
    ...createAppearanceCommands(props),
  ]
}
